/**
 * Client actions: what the server agent asks the browser to apply (CONTRACTS-W2 §C
 * `client_action`). The server has already validated every edit plan against the blocks the
 * browser sent; here the plan lands in the live editor through the editor bridge with review on,
 * so every change is a suggestion the user accepts or rejects (CONTRACTS §6).
 *
 * Kinds:
 *  - doc_edit_plan {cardId, documentId, templateId, ops, label, summary, allowLargeDeletion}
 *    Each action carries the turn's WHOLE merged plan for that surface; applying it supersedes
 *    the previous suggestion set (applyEditPlan works on the pre-review baseline).
 *  - navigate {path, documentId?}
 *  - open_settings {}
 */

import { getEditorHandle, supportsEditPlans } from "./editorBridge";
import { prepareOpsForEditor } from "./editHtml";

/**
 * Apply an edit plan to the open editor.
 * @param {object} payload - Action payload.
 * @param {() => object|null} getEditorRef - Active editor getter.
 * @returns {{ok: boolean, status: string, reviewId: string|null, changeCount: number, failedCount: number, failedReasons: string[], firstBlockId: string|null, error?: string}} Outcome.
 */
export const applyDocEditPlan = (payload, getEditorRef) => {
  const fail = (error, status = "failed") => ({ ok: false, status, reviewId: null, changeCount: 0, failedCount: (payload?.ops || []).length, failedReasons: [error], firstBlockId: null, error });
  const handle = getEditorHandle(getEditorRef);
  if (!handle) return fail("The document is no longer open, so the suggestions could not be shown.", "not_open");
  let meta = null;
  try {
    meta = typeof handle.getMeta === "function" ? handle.getMeta() : null;
  } catch {
    meta = null;
  }
  const sameSurface = !meta || (payload.documentId ? meta.documentId === payload.documentId : payload.templateId ? meta.templateId === payload.templateId : true);
  if (!sameSurface) return fail("A different document is open now, so the suggestions were not applied.", "not_open");
  if (meta?.readOnly) return fail("The editor is in preview mode, so the suggestions were not applied.");
  if (!supportsEditPlans(handle)) return fail("This editor cannot show block suggestions.");

  // Image aliases (sent in place of base64 data, see surfaceBridge.capBlocks) are restored from
  // this editor's own images; anything that would load or run on display is stripped.
  let liveBlocks = [];
  try {
    liveBlocks = handle.getBlocks() || [];
  } catch {
    liveBlocks = [];
  }
  const { ops } = prepareOpsForEditor(Array.isArray(payload.ops) ? payload.ops : [], liveBlocks);
  const label = payload.label || "Inky's edit";
  const options = { review: true, label, ...(payload.allowLargeDeletion ? { allowLargeDeletion: true } : {}) };
  const draft = ops.find((op) => op?.op === "replace_document");
  if (draft) {
    if (typeof handle.setContentWithReview !== "function") return fail("This editor cannot show a drafted document for review.");
    const outcome = handle.setContentWithReview(draft.html || "", options) || {};
    if (outcome.refused) return fail(`The editor refused the draft (${outcome.refused}).`);
    if (!outcome.reviewId && outcome.hasChanges === false) return fail("The draft matched the document, so nothing changed.", "no_changes");
    return { ok: true, status: "pending_review", reviewId: outcome.reviewId || null, changeCount: 1, failedCount: 0, failedReasons: [], firstBlockId: null };
  }

  let outcome;
  try {
    outcome = handle.applyEditPlan(ops, options) || {};
  } catch (error) {
    return fail(`The editor could not apply the suggestions: ${error?.message || error}`);
  }
  const applied = Array.isArray(outcome.applied) ? outcome.applied : [];
  const failed = Array.isArray(outcome.failed) ? outcome.failed : [];
  const failedReasons = failed.map((item) => String(item?.reason || "")).filter(Boolean);
  if (!applied.length) {
    return { ...fail(failedReasons[0] || "The editor refused every change."), failedCount: failed.length || ops.length, failedReasons };
  }
  return {
    ok: true,
    status: "pending_review",
    reviewId: outcome.reviewId || null,
    changeCount: applied.length,
    failedCount: failed.length,
    failedReasons,
    firstBlockId: applied.map((item) => item?.op?.blockId || item?.blockId).find(Boolean) || null,
  };
};

/**
 * Apply one client action.
 * @param {{kind: string, payload: object}} action - Action from the stream.
 * @param {object} env - Browser capabilities.
 * @param {() => object|null} env.getEditorRef - Active editor getter.
 * @param {(path: string) => void} env.navigate - In-app navigation.
 * @param {(target: {documentId: string}) => void} [env.openDocument] - Open a document in the workspace.
 * @param {() => void} [env.markNavigated] - Record that the turn moved the user.
 * @returns {object} Outcome (`ok` plus kind-specific fields).
 */
export const applyClientAction = (action, env) => {
  const payload = action?.payload && typeof action.payload === "object" ? action.payload : {};
  switch (action?.kind) {
    case "doc_edit_plan":
      return applyDocEditPlan(payload, env.getEditorRef);
    case "navigate": {
      const path = String(payload.path || "");
      if (!path.startsWith("/") || path.startsWith("//")) return { ok: false, error: "Invalid path." };
      env.markNavigated?.();
      if (payload.documentId && typeof env.openDocument === "function") env.openDocument({ documentId: payload.documentId });
      else env.navigate(path);
      return { ok: true };
    }
    case "open_settings":
      try {
        window.dispatchEvent(new Event("quillai:open-settings"));
      } catch {
        return { ok: false, error: "Settings are unavailable here." };
      }
      return { ok: true };
    default:
      return { ok: false, error: `Unknown action ${action?.kind}.` };
  }
};

/**
 * Fold client-action outcomes into the saved reply's cards (status, review id, real counts).
 * @param {object|null} toolCall - Saved `tool_call` ({v: 2, steps, cards}).
 * @param {Map<string, object>} outcomes - Outcome per card id.
 * @returns {object|null} Patched tool_call, or null when nothing changed.
 */
export const patchCardsWithOutcomes = (toolCall, outcomes) => {
  if (!toolCall || !Array.isArray(toolCall.cards) || !outcomes?.size) return null;
  let changed = false;
  const cards = toolCall.cards.map((card) => {
    const outcome = outcomes.get(card.id);
    if (!outcome) return card;
    changed = true;
    return {
      ...card,
      status: outcome.ok ? "pending_review" : "failed",
      reviewId: outcome.reviewId || null,
      changeCount: outcome.ok ? outcome.changeCount : 0,
      failedCount: outcome.failedCount || 0,
      ...(outcome.failedReasons?.length ? { failedReasons: outcome.failedReasons.slice(0, 5) } : {}),
      ...(outcome.firstBlockId ? { firstBlockId: outcome.firstBlockId } : {}),
      ...(outcome.ok ? {} : { outcome: outcome.error }),
    };
  });
  return changed ? { ...toolCall, cards } : null;
};
